import { Space } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';
import cn from 'classnames';
import React, { type ComponentProps, useCallback, useState } from 'react';

import { QUERY_PARAMS } from 'constants/queryNames';
import { APP_PAGES_NAMES } from 'constants/routes';

import { FormStepId } from 'shared/config/formStepId';
import { useAppSelector } from 'shared/lib';
import { sendEventChangeFilter, sendEventGoToOfferWebsite } from 'shared/lib/sendGAEvents';
import { AdaptiveCard } from 'shared/ui/AdaptiveCard';
import { AppContainer } from 'shared/ui/AppContainer';
import { FormProgress } from 'shared/ui/FormProgress';
import { NoSSR } from 'shared/ui/NoSSR';

import { hasAnySeoDataSelector, SeoBreadcrumbs, SeoPrefooter } from 'entities/metadata';

import { AnketaTitle } from 'features/AnketaTitle';
import { useClientRedirect } from 'features/useClientRedirect';

import { useAutoValidation, VitrinsAnketa } from 'widgets/AnketaWidget';
import { SeoBlocks } from 'widgets/SeoBlocks';
import { VitrinsCards } from 'widgets/VitrinsCards';
import { VitrinsHeader } from 'widgets/VitrinsHeader';
import { VitrinsOffers } from 'widgets/VitrinsOffers';

import styles from './VitrinsPage.module.scss';

export const VitrinsPage = () => {
  const isMobile = useIsMobile();
  const blockSpacing = isMobile ? 32 : 48;
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { push } = useClientRedirect();
  const hasAnySeoData = useAppSelector(hasAnySeoDataSelector);

  const onFormComplete = useCallback(
    (isValid: boolean) => {
      setIsRedirecting(true);
      setTimeout(() => {
        if (isValid) {
          push(APP_PAGES_NAMES.LOANS_OFFERS);
        } else {
          push(APP_PAGES_NAMES.NEW_FORM, `${QUERY_PARAMS.FORM_STEP_ID}=${FormStepId.PassportInfo}`);
        }
      }, 300);
    },
    [push, setIsRedirecting],
  );

  const { validate } = useAutoValidation(false, {
    onFormValidationSuccess: () => onFormComplete(true),
    onError: () => onFormComplete(false),
  });

  const onChangeFilter: NonNullable<ComponentProps<typeof VitrinsOffers>['onChangeFilter']> = useCallback(
    ({ value }) => {
      sendEventChangeFilter(value);
    },
    [],
  );

  const onOfferSubmit: NonNullable<ComponentProps<typeof VitrinsOffers>['onOfferSubmit']> = useCallback(
    async (item) => {
      if (item.type === 'credit') {
        const { organization, sum, term, rate, psk } = item;
        sendEventGoToOfferWebsite({
          id: organization,
          sum,
          term,
          rate: rate || '',
          psk: psk || '',
        });
      }

      await push(APP_PAGES_NAMES.NEW_FORM);
    },
    [push],
  );

  return (
    <Space
      direction="vertical"
      size={60}
    >
      <div className={styles.wrapper}>
        <Space
          direction="vertical"
          size={blockSpacing}
          className={styles.blocks}
        >
          <VitrinsHeader />
          <VitrinsCards />
        </Space>
        <Space
          direction="vertical"
          className={styles.formBlock}
        >
          <Space
            direction="vertical"
            size={isMobile ? 16 : 24}
            className={styles.formBlockContent}
          >
            <AnketaTitle
              productType="Credit"
              level={2}
              className={styles.formTitle}
            />
            <NoSSR>
              <AdaptiveCard
                desktopSize={24}
                color="light"
                isVertical
              >
                <Space
                  direction="vertical"
                  size={isMobile ? 24 : 32}
                >
                  <FormProgress
                    currentStep={1}
                    totalSteps={4}
                  />
                  <VitrinsAnketa
                    onFormComplete={validate}
                    isLoading={isRedirecting}
                  />
                </Space>
              </AdaptiveCard>
            </NoSSR>
          </Space>
        </Space>
        <Space
          direction="vertical"
          size={blockSpacing}
          className={styles.blocks}
        >
          <Space
            direction="vertical"
            size={32}
            className={cn({ 'h-pl-16 h-pr-16': isMobile })}
          >
            <VitrinsOffers
              isGrayBackground
              onChangeFilter={onChangeFilter}
              onOfferSubmit={onOfferSubmit}
            />
          </Space>
        </Space>
      </div>
      {hasAnySeoData && (
        <AppContainer
          hidePaddings
          className={styles.seo}
        >
          <SeoBlocks />
          <SeoPrefooter />
          <div className={isMobile ? 'h-ml-16' : undefined}>
            <SeoBreadcrumbs />
          </div>
        </AppContainer>
      )}
    </Space>
  );
};
